import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scientificPartnerApi } from '@/features/workspace/api';
import { useToast } from '@/hooks/useToast';
import {
  BriefcaseIcon,
  FileTextIcon,
  CheckCircle2Icon,
  UploadIcon,
  HistoryIcon,
  XIcon,
  ArrowLeftIcon,
  ChevronRightIcon
} from './PartnerIcons';

interface OrderFile {
  filename: string;
  version_label: string;
  size_label: string;
  status: string;
  updated_at_label: string;
}

interface OrderAudit {
  actor_name: string;
  action: string;
  timestamp_label: string;
  note?: string | null;
}

interface ClientOrder {
  code: string;
  contour: string;
  title: string;
  client_or_author: string;
  status: string;
  next_step: string;
  deadline: string;
  linked_article_title?: string | null;
  linked_client_code?: string | null;
  details?: string | null;
  files?: OrderFile[];
  history?: OrderAudit[];
}

export const PartnerClientOrdersView: React.FC = () => {
  const navigate = useNavigate();
  const { notify } = useToast();
  const [activeTab, setActiveTab] = useState<'all' | 'orders' | 'clients' | 'articles' | 'coauthors'>('all');
  const [items, setItems] = useState<ClientOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedOrder, setSelectedOrder] = useState<ClientOrder | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [newStatus, setNewStatus] = useState<string>('В работе');
  const [statusNote, setStatusNote] = useState<string>('');
  const [uploadFilename, setUploadFilename] = useState<string>('');
  const [uploadVersion, setUploadVersion] = useState<string>('');
  const [showUploadForm, setShowUploadForm] = useState<boolean>(false);
  const [notice, setNotice] = useState<string | null>(null);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const res = await scientificPartnerApi.module('client_orders', { tab: activeTab });
      if (res && Array.isArray(res.items)) {
        setItems(res.items as ClientOrder[]);
      } else {
        setItems([]);
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [activeTab]);

  const handleOpenDetail = async (order: ClientOrder) => {
    try {
      const detail = await scientificPartnerApi.clientOrderDetail(order.code);
      setSelectedOrder(detail as ClientOrder);
      setNewStatus((detail as ClientOrder).status || order.status);
      setShowUploadForm(false);
      setUploadFilename('');
      setUploadVersion('');
    } catch {
      setSelectedOrder(order);
      setNewStatus(order.status);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder) return;
    try {
      setUpdatingStatus(true);
      const updated = await scientificPartnerApi.updateClientOrderStatus(selectedOrder.code, {
        status: newStatus,
        note: statusNote || undefined
      });
      if (!['code', 'contour', 'title', 'client_or_author', 'status', 'next_step', 'deadline'].every((key) => typeof updated[key] === 'string')) {
        throw new Error('Некорректный ответ сервера');
      }
      setSelectedOrder({
        ...updated,
        code: String(updated.code), contour: String(updated.contour), title: String(updated.title),
        client_or_author: String(updated.client_or_author), status: String(updated.status),
        next_step: String(updated.next_step), deadline: String(updated.deadline),
      });
      setItems((prev) =>
        prev.map((item) => (item.code === (updated as ClientOrder).code ? { ...item, ...(updated as ClientOrder) } : item))
      );
      setStatusNote('');
      setNotice('Статус заказа обновлён.');
      setTimeout(() => setNotice(null), 3500);
    } catch {
      notify('Не удалось обновить статус заказа', 'error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleUploadVersion = async () => {
    if (!selectedOrder || !uploadFilename.trim() || !uploadVersion.trim()) return;
    try {
      setUploading(true);
      const updated = await scientificPartnerApi.addOrderFile(selectedOrder.code, {
        filename: uploadFilename.trim(),
        version_label: uploadVersion.trim(),
        size_label: '1.2 MB',
      });
      if (!['code', 'contour', 'title', 'client_or_author', 'status', 'next_step', 'deadline'].every((key) => typeof updated[key] === 'string')) {
        throw new Error('Некорректный ответ сервера');
      }
      setSelectedOrder({
        ...updated,
        code: String(updated.code), contour: String(updated.contour), title: String(updated.title),
        client_or_author: String(updated.client_or_author), status: String(updated.status),
        next_step: String(updated.next_step), deadline: String(updated.deadline),
      });
      setShowUploadForm(false);
      setUploadFilename('');
      setUploadVersion('');
      setNotice(`Версия ${uploadVersion.trim()} файла «${uploadFilename.trim()}» прикреплена к заказу.`);
      setTimeout(() => setNotice(null), 3500);
    } catch {
      notify('Не удалось загрузить файл заказа', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border/40 pb-4">
        <div>
          <button
            onClick={() => navigate('/app/partner')}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground mb-2 transition-colors"
          >
            <ArrowLeftIcon size={14} />
            В кабинет научного партнёра
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center font-bold">
              <BriefcaseIcon size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-foreground">
                Заказы клиентов
              </h1>
              <p className="text-xs text-muted-foreground">
                Единый центр коммерческих и соавторских заказов, сопровождение публикаций и клиентский сервис
              </p>
            </div>
          </div>
        </div>
      </div>

      {notice && (
        <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-300 rounded-xl text-xs flex items-center gap-2">
          <CheckCircle2Icon size={16} className="flex-shrink-0" />
          <span>{notice}</span>
        </div>
      )}

      <div className="flex items-center gap-2 border-b border-border/40 pb-2 overflow-x-auto">
        {(
          [
            { id: 'all', label: 'Все заказы' },
            { id: 'orders', label: 'Договоры и заказы' },
            { id: 'clients', label: 'Клиенты' },
            { id: 'articles', label: 'Статьи' },
            { id: 'coauthors', label: 'Совместные публикации' }
          ] as const
        ).map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-primary text-primary-foreground shadow-xs'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-xs text-muted-foreground">Загрузка заказов...</div>
      ) : items.length === 0 ? (
        <div className="p-12 text-center border border-dashed rounded-2xl text-xs text-muted-foreground">
          Заказов в выбранной категории не найдено
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map(order => (
            <div
              key={order.code}
              onClick={() => handleOpenDetail(order)}
              className="p-4 rounded-xl border border-border/60 bg-card hover:border-primary/50 hover:shadow-md transition-all cursor-pointer flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-mono font-bold px-2 py-0.5 rounded bg-muted text-foreground">
                      {order.code}
                    </span>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600">
                      {order.status}
                    </span>
                  </div>
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase">
                    {order.contour}
                  </span>
                </div>

                <h3 className="text-sm font-bold text-foreground leading-snug mb-1">
                  {order.linked_article_title || order.title}
                </h3>
                <p className="text-xs text-muted-foreground mb-3">
                  Клиент / автор: <strong className="text-foreground">{order.client_or_author}</strong>
                </p>

                <div className="grid grid-cols-2 gap-2 text-[11px] text-muted-foreground mb-3 bg-muted/20 p-2.5 rounded-lg border border-border/30">
                  <div>
                    Следующий шаг:{' '}
                    <span className="text-foreground font-medium">{order.next_step || '—'}</span>
                  </div>
                  <div>
                    Срок: <span className="text-foreground font-medium">{order.deadline || '—'}</span>
                  </div>
                </div>
              </div>

              <div className="cabinet-cta cabinet-cta--emerald">
                <span>Подробная карточка и файлы</span>
                <ChevronRightIcon size={16} />
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedOrder && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-card w-full max-w-3xl rounded-2xl border border-border shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
            <div className="p-4 border-b border-border/50 flex items-center justify-between bg-muted/20">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                  <BriefcaseIcon size={20} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-bold text-primary">{selectedOrder.code}</span>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600">
                      {selectedOrder.status}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-foreground truncate max-w-lg">
                    {selectedOrder.linked_article_title || selectedOrder.title}
                  </h3>
                </div>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="p-1 rounded-md text-muted-foreground hover:text-foreground"
              >
                <XIcon size={16} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div className="space-y-1.5 p-3 rounded-xl border border-border/50 bg-card">
                  <span className="text-[10px] uppercase font-bold text-muted-foreground">Клиент / Заказчик</span>
                  <div className="text-sm font-bold text-foreground">{selectedOrder.client_or_author}</div>
                  <div className="text-muted-foreground">
                    Код клиента: <strong className="text-foreground">{selectedOrder.linked_client_code || '—'}</strong>
                  </div>
                </div>

                <div className="space-y-1.5 p-3 rounded-xl border border-border/50 bg-card">
                  <span className="text-[10px] uppercase font-bold text-muted-foreground">Статус и срок</span>
                  <div className="text-sm font-bold text-foreground">{selectedOrder.status}</div>
                  <div className="text-muted-foreground">
                    Следующий шаг: <strong className="text-foreground">{selectedOrder.next_step}</strong>
                  </div>
                  <div className="text-muted-foreground">
                    Дедлайн: <strong className="text-foreground">{selectedOrder.deadline}</strong>
                  </div>
                </div>
              </div>

              {selectedOrder.details ? (
                <div className="p-3 rounded-xl border border-border/50 bg-muted/20 text-xs text-muted-foreground">
                  {selectedOrder.details}
                </div>
              ) : null}

              <div className="p-3 bg-card rounded-xl border border-border/50 space-y-2">
                <div className="text-xs font-bold text-foreground">Обновить статус и стадию заказа</div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <select
                    value={newStatus}
                    onChange={e => setNewStatus(e.target.value)}
                    className="text-xs p-2 rounded-lg border border-border bg-background text-foreground"
                  >
                    <option value="В работе">В работе</option>
                    <option value="На согласовании">На согласовании</option>
                    <option value="Активен">Активен</option>
                    <option value="Завершено">Завершено</option>
                  </select>
                  <input
                    type="text"
                    value={statusNote}
                    onChange={e => setStatusNote(e.target.value)}
                    placeholder="Комментарий к изменению статуса (для аудита)..."
                    className="flex-1 text-xs px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground"
                  />
                  <button
                    onClick={handleUpdateStatus}
                    disabled={updatingStatus}
                    className="px-4 py-2 text-xs font-semibold bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
                  >
                    {updatingStatus ? 'Сохранение...' : 'Обновить'}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                    <FileTextIcon size={16} className="text-blue-500" />
                    Репозиторий файлов и версий статьи
                  </span>
                  <button
                    onClick={() => setShowUploadForm((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 bg-primary/10 text-primary hover:bg-primary/20 rounded-lg transition-colors"
                  >
                    <UploadIcon size={14} />
                    Загрузить новую версию
                  </button>
                </div>

                {showUploadForm && (
                  <div className="p-3 rounded-xl border border-border/50 bg-muted/20 flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      value={uploadFilename}
                      onChange={(e) => setUploadFilename(e.target.value)}
                      placeholder="Имя файла (напр: draft_v2.docx)"
                      className="flex-1 text-xs px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                    />
                    <input
                      type="text"
                      value={uploadVersion}
                      onChange={(e) => setUploadVersion(e.target.value)}
                      placeholder="Версия (v1.1)"
                      className="w-28 text-xs px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                    />
                    <button
                      type="button"
                      disabled={uploading || !uploadFilename.trim() || !uploadVersion.trim()}
                      onClick={handleUploadVersion}
                      className="px-3 py-2 text-xs font-semibold bg-primary text-primary-foreground rounded-lg disabled:opacity-50"
                    >
                      {uploading ? 'Загрузка...' : 'Прикрепить'}
                    </button>
                  </div>
                )}

                <div className="space-y-1.5">
                  {(selectedOrder.files && selectedOrder.files.length > 0) ? (
                    selectedOrder.files.map((f, idx) => (
                      <div
                        key={`${f.filename}-${f.version_label}-${idx}`}
                        className="p-2.5 rounded-lg border border-border/50 bg-background flex items-center justify-between text-xs"
                      >
                        <div className="flex items-center gap-2.5 truncate">
                          <FileTextIcon size={16} className="text-muted-foreground flex-shrink-0" />
                          <span className="font-medium text-foreground truncate">{f.filename}</span>
                          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            {f.version_label}
                          </span>
                          <span className="text-[10px] text-muted-foreground">{f.size_label}</span>
                        </div>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                          {f.status} · {f.updated_at_label}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="p-3 rounded-lg border border-dashed border-border/50 text-xs text-muted-foreground text-center">
                      Файлы ещё не загружены
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                  <HistoryIcon size={16} className="text-amber-500" />
                  История аудита и действий по заказу
                </span>
                <div className="p-3 rounded-xl border border-border/40 bg-muted/20 space-y-2 text-xs">
                  {(selectedOrder.history && selectedOrder.history.length > 0) ? (
                    selectedOrder.history.map((a, idx) => (
                      <div key={`${a.action}-${idx}`} className="flex items-start justify-between gap-2 border-b border-border/20 pb-1.5 last:border-0 last:pb-0">
                        <div>
                          <div className="font-semibold text-foreground">{a.action}</div>
                          <div className="text-[11px] text-muted-foreground">
                            {a.note || 'Без комментария'} ({a.actor_name})
                          </div>
                        </div>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                          {a.timestamp_label}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-muted-foreground">История аудита пуста</div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-border/50 flex items-center justify-between bg-muted/20">
              <button
                onClick={() => {
                  navigate('/app/partner/academic_connections');
                }}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
              >
                <BriefcaseIcon size={16} />
                Написать клиенту в контактах
              </button>
              <button
                onClick={() => setSelectedOrder(null)}
                className="px-4 py-1.5 text-xs font-semibold bg-foreground text-background rounded-lg hover:opacity-90"
              >
                Закрыть карточку
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
